import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import EntityForm from "./EntityForm";

// ---------------------------------------------------------------------------
// Reusable, backend-backed CRUD shell for the Mantenimiento mini-app. Unlike
// the Nuvex scaffold's CrudSection (mock, in-memory) this one is data-agnostic:
// the parent owns the rows and the create/update/delete async handlers, so the
// same shell drives every maintenance list against the real API.
// ---------------------------------------------------------------------------

export type Row = Record<string, unknown> & { id: string };

export interface Column {
  key: string;
  label: string;
  align?: "left" | "center" | "right";
  width?: string;
  render?: (row: Row) => React.ReactNode;
}

export interface RepeatColumn {
  key: string;
  label: string;
  type?: "text" | "number";
}

export interface FormField {
  key: string;
  label: string;
  type?: "text" | "number" | "textarea" | "select" | "date" | "repeat";
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  defaultValue?: string | number;
  /** Columns for a `repeat` (line-items) field, e.g. parts or services. */
  repeatColumns?: RepeatColumn[];
  /** Label for the "add row" button of a `repeat` field. */
  addRowLabel?: string;
}

interface ApiCrudSectionProps {
  title: string;
  subtitle?: string;
  icon?: string;
  columns: Column[];
  fields: FormField[];
  rows: Row[];
  loading?: boolean;
  error?: string | null;
  searchKey?: string;
  readOnly?: boolean;
  addLabel?: string;
  /** Toolbar actions rendered to the left of the "add" button. */
  toolbar?: React.ReactNode;
  /** Extra per-row actions rendered before edit/delete. */
  extraActions?: (row: Row) => React.ReactNode;
  /** Dentro de otra página (pestaña): sin título ni padding propios. */
  embedded?: boolean;
  onCreate?: (values: Record<string, string>) => Promise<void> | void;
  onUpdate?: (id: string, values: Record<string, string>) => Promise<void> | void;
  onDelete?: (id: string) => Promise<void> | void;
}

export default function ApiCrudSection({
  title,
  subtitle,
  icon,
  columns,
  fields,
  rows,
  loading = false,
  error = null,
  searchKey,
  readOnly = false,
  addLabel,
  toolbar,
  extraActions,
  onCreate,
  onUpdate,
  onDelete,
  embedded = false,
}: ApiCrudSectionProps) {
  const { t } = useTranslation("common");
  const tc = buildThemeClasses();

  const [query, setQuery] = useState("");
  const [modal, setModal] = useState<null | { mode: "add" | "edit"; row?: Row }>(
    null
  );
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const key = searchKey ?? columns[0]?.key;
  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) =>
      String(r[key] ?? "").toLowerCase().includes(q)
    );
  }, [rows, query, key]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = filtered.slice(
    safePage * pageSize,
    safePage * pageSize + pageSize
  );

  const closeModal = () => setModal(null);

  const handleSubmit = async (values: Record<string, string>) => {
    if (modal?.mode === "edit" && modal.row) {
      await onUpdate?.(modal.row.id, values);
    } else {
      await onCreate?.(values);
    }
    closeModal();
  };

  const cellAlign = (a?: string) =>
    a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

  const hasRowActions = !readOnly || !!extraActions;

  return (
    <div className={embedded ? "w-full" : "h-full w-full overflow-y-auto p-6"}>
      <div className={embedded ? "w-full" : "mx-auto max-w-6xl"}>
        {/* Header */}
        <div className={`flex flex-wrap items-center justify-between gap-4 ${embedded ? "mb-4" : "mb-6"}`}>
          <div className={embedded ? "hidden" : "flex items-center gap-3"}>
            {icon && (
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
                <i className={`${icon} text-xl`} />
              </span>
            )}
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>{title}</h1>
              {subtitle && (
                <p className={`text-sm ${tc.textSecondary}`}>{subtitle}</p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {toolbar}
            <div
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}
            >
              <i className={`ri-search-line ${tc.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("nuvexAccess.search")}
                className={`bg-transparent text-sm outline-none ${tc.text}`}
              />
            </div>
            {!readOnly && onCreate && (
              <button
                onClick={() => setModal({ mode: "add" })}
                className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400"
              >
                <i className="ri-add-line" />
                <span className="whitespace-nowrap">
                  {addLabel ?? t("nuvexAccess.add")}
                </span>
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-500">
            <i className="ri-error-warning-line mr-1" />
            {error}
          </div>
        )}

        {/* Table */}
        <div
          className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}
        >
          <div className="w-full overflow-x-auto">
            <table className="w-full overflow-hidden rounded-xl text-xs shadow-sm">
              <thead className="bg-subtle/60 text-[11px] uppercase tracking-wide text-content-muted">
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      style={{ width: c.width }}
                      className={`p-3 font-bold ${cellAlign(c.align)}`}
                    >
                      {c.label}
                    </th>
                  ))}
                  {hasRowActions && (
                    <th className="p-3 text-center font-bold">
                      {t("mantenimiento.actions")}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={columns.length + (hasRowActions ? 1 : 0)}
                      className="p-6 text-center bg-card text-content-muted"
                    >
                      <i className="ri-loader-4-line animate-spin mr-1" />
                      {t("mantenimiento.loading")}
                    </td>
                  </tr>
                ) : pageRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length + (hasRowActions ? 1 : 0)}
                      className="p-6 text-center bg-card text-content-muted"
                    >
                      {t("nuvexAccess.empty")}
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row, i) => (
                    <tr
                      key={row.id}
                      className={`text-content transition-colors hover:bg-subtle/40 ${i % 2 === 0 ? "bg-card" : "bg-surface/50"}`}
                    >
                      {columns.map((c) => (
                        <td key={c.key} className={`p-2 ${cellAlign(c.align)}`}>
                          {c.render ? c.render(row) : String(row[c.key] ?? "")}
                        </td>
                      ))}
                      {hasRowActions && (
                        <td className="p-2">
                          <div className="flex items-center justify-center gap-1">
                            {extraActions?.(row)}
                            {!readOnly && onUpdate && (
                              <button
                                title={t("nuvexAccess.edit")}
                                onClick={() => setModal({ mode: "edit", row })}
                                className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
                              >
                                <i className="ri-edit-line" />
                              </button>
                            )}
                            {!readOnly && onDelete && (
                              <button
                                title={t("mantenimiento.delete")}
                                onClick={() => onDelete(row.id)}
                                className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
                              >
                                <i className="ri-delete-bin-line" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {filtered.length > pageSize && (
            <div className="mt-3 flex items-center justify-center gap-3 text-sm">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                className="rounded-lg px-3 py-1 disabled:opacity-40 hover:bg-primary-500/10"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              <span className="text-content-secondary">
                {safePage + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
                className="rounded-lg px-3 py-1 disabled:opacity-40 hover:bg-primary-500/10"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <Modal
          title={
            modal.mode === "edit"
              ? `${t("nuvexAccess.edit")} — ${title}`
              : `${t("nuvexAccess.new")} — ${title}`
          }
          onClose={closeModal}
          themeClasses={tc}
          content={
            <EntityForm
              fields={fields}
              initial={modal.row}
              themeClasses={tc}
              submitLabel={
                modal.mode === "edit"
                  ? t("nuvexAccess.save")
                  : t("nuvexAccess.create")
              }
              onCancel={closeModal}
              onSubmit={handleSubmit}
            />
          }
        />
      )}
    </div>
  );
}
